import { useState } from "react";
import { Link } from "react-router";
import { useActors, useNodes } from "../api.ts";
import { reachableHubOrigin } from "../guide.ts";
import { nodeInitCommand, noWorkerReason, type NoWorkerReason } from "../lib/office-ready.ts";
import { Button } from "../ui.tsx";

/**
 * Why no agent can work right now, live (node.* and actor.* events refetch the two queries). `undefined`
 * until both lists load, so nothing cries wolf on first paint; `null` when the office can work.
 */
export function useNoWorkerReason(): NoWorkerReason | undefined {
  const actors = useActors();
  const nodes = useNodes();
  if (!actors.data || !nodes.data) return undefined;
  return noWorkerReason(actors.data, nodes.data);
}

/*
 * Shown while nothing can happen: no active agent, or no online machine hosting one. It has no
 * dismiss control on purpose — it goes away only when the office can work, live. It never shows a
 * token: the command reads it from stdin.
 */
export function NoWorkersBanner({ className = "" }: { className?: string }) {
  const reason = useNoWorkerReason();
  const [copied, setCopied] = useState(false);
  if (!reason) return null;
  // Null on a loopback origin: the placeholder then stands in, and the hint says what to put there.
  const hubOrigin = reachableHubOrigin(window.location);
  const command = nodeInitCommand(hubOrigin);
  const copy = () => {
    navigator.clipboard?.writeText(command).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  };
  return (
    <section className={`rounded-2xl border border-rule bg-paper-2 p-5 text-[14px] ${className}`} role="status" aria-label="No agent can work yet" data-testid="no-workers" data-reason={reason}>
      <p className="text-[16px] font-medium" data-testid="no-workers-message">
        Your ideas are saved, but no agent can work on them yet.
      </p>
      <p className="mt-1 text-ink-2">
        {reason === "no-agents"
          ? "Nothing runs until the office has an agent and a machine to run it on."
          : "You have agents, but none is on a machine that is online. Agents run on a machine you connect."}{" "}
        Specs and tasks wait until then.
      </p>
      <ol className="mt-3 list-decimal space-y-1 pl-5 text-ink-2">
        <li>
          On the machine that will do the work, run this in a terminal. It asks for a token on stdin; <Link to="/team" className="underline decoration-rule hover:text-ink">create one on Team</Link> if you need it.
        </li>
        <li>
          Start the node, then {reason === "no-agents" ? <Link to="/team" className="underline decoration-rule hover:text-ink">add an agent</Link> : "start an agent on it"}. This message disappears by itself once an agent is online.
        </li>
      </ol>
      <div className="mt-3 flex items-start gap-2">
        <pre className="min-w-0 flex-1 overflow-x-auto rounded-md border border-rule bg-paper p-2 font-mono text-[12px]" data-testid="no-workers-command">{command}</pre>
        <Button type="button" onClick={copy} data-testid="no-workers-copy">
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      {!hubOrigin && (
        <p className="mt-2 text-ink-2" data-testid="no-workers-hub-hint">
          Replace &lt;hub-address&gt; with the address of this hub as that machine reaches it, for example the host name you open Roomate at from there.
        </p>
      )}
    </section>
  );
}
